import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api, useAuth } from '../context/AuthContext';
import { Send, ImagePlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 5000;
const MAX_IMAGE_BYTES = 750 * 1024; // ~750 KB after base64 ≈ 1 MB payload

const TeamChat = ({ height = '70vh' }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [sending, setSending] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  const scrollToBottom = (smooth = true) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  };

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get('/api/team-chat/messages?limit=100');
      const msgs = res.data || [];
      const newLastId = msgs.length ? msgs[msgs.length - 1].id : null;
      const hadNew = newLastId && newLastId !== lastMessageIdRef.current;
      setMessages(msgs);
      lastMessageIdRef.current = newLastId;
      // Tell backend we've seen these
      if (msgs.length) {
        api.post('/api/team-chat/mark-read').catch(() => {});
      }
      if (hadNew) {
        setTimeout(() => scrollToBottom(true), 50);
      }
    } catch (err) {
      console.error('team chat poll failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling loop (pauses while the tab is hidden)
  useEffect(() => {
    let cancelled = false;

    const start = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (!cancelled && !document.hidden) fetchMessages();
      }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    fetchMessages().then(() => {
      if (!cancelled) setTimeout(() => scrollToBottom(false), 50);
    });
    start();
    const onVis = () => {
      if (document.hidden) stop();
      else { fetchMessages(); start(); }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchMessages]);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image must be under ${Math.round(MAX_IMAGE_BYTES / 1024)} KB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setImageDataUrl(ev.target.result);
    reader.onerror = () => toast.error('Failed to read image');
    reader.readAsDataURL(file);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body && !imageDataUrl) return;

    setSending(true);
    try {
      const res = await api.post('/api/team-chat/messages', {
        text: body,
        image_data_url: imageDataUrl,
      });
      setMessages((prev) => [...prev, res.data]);
      lastMessageIdRef.current = res.data.id;
      setText('');
      setImageDataUrl(null);
      setTimeout(() => scrollToBottom(true), 50);
    } catch (err) {
      console.error('send failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const isMine = (m) => currentUser && m.sender_id === currentUser.id;

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch {
      return '';
    }
  };

  return (
    <div
      className="flex flex-col bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden"
      style={{ height }}
      data-testid="team-chat"
    >
      {/* Message list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        data-testid="team-chat-messages"
      >
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-lime-400" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-zinc-500">
            <p className="text-lg font-semibold">No messages yet 💬</p>
            <p className="text-sm mt-1">Start the conversation with your team</p>
          </div>
        )}

        {!loading && messages.map((m) => {
          const mine = isMine(m);
          return (
            <div
              key={m.id}
              className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              data-testid={`team-chat-msg-${m.id}`}
            >
              <div className={`max-w-[78%] ${mine ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {!mine && (
                  <div className="text-xs text-zinc-400 px-1">
                    <span className="font-semibold text-zinc-200">{m.sender_name}</span>
                    {m.sender_role === 'admin' && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-semibold">
                        ADMIN
                      </span>
                    )}
                  </div>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 ${
                    mine
                      ? 'bg-lime-500 text-black rounded-br-sm'
                      : 'bg-zinc-800 text-white rounded-bl-sm'
                  }`}
                >
                  {m.image_data_url && (
                    <img
                      src={m.image_data_url}
                      alt="attachment"
                      className="rounded-lg max-h-64 object-cover mb-1"
                      loading="lazy"
                    />
                  )}
                  {m.text && <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>}
                </div>
                <span className={`text-[10px] ${mine ? 'text-zinc-500' : 'text-zinc-500'} px-1`}>
                  {formatTime(m.created_at)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Image preview (before send) */}
      {imageDataUrl && (
        <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-950 flex items-center gap-2">
          <img src={imageDataUrl} alt="preview" className="h-16 w-16 object-cover rounded-lg" />
          <div className="flex-1 text-xs text-zinc-400">Image attached</div>
          <button
            onClick={() => setImageDataUrl(null)}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400"
            data-testid="team-chat-remove-image"
            aria-label="remove image"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 p-3 pb-14 sm:pb-3 border-t border-zinc-800 bg-zinc-950"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImagePick}
          data-testid="team-chat-image-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400"
          data-testid="team-chat-pick-image-btn"
          aria-label="attach image"
        >
          <ImagePlus className="w-5 h-5" />
        </button>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Message the team…"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-lime-500"
          data-testid="team-chat-input"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={sending || (!text.trim() && !imageDataUrl)}
          className="p-2 rounded-lg bg-lime-500 text-black hover:bg-lime-600 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="team-chat-send-btn"
          aria-label="send"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
};

export default TeamChat;
